import { createTamagui, createFont } from 'tamagui';
import { themes } from './themes';
import { defaultConfig } from '@tamagui/config/v4';
import { tamaguiLightColors, tamaguiDarkColors } from './tamagui-colors';
import { responsiveFontSize } from './stylesheet';

// Use colors from the marketing website color palette
const lightColors = tamaguiLightColors;
const darkColors = tamaguiDarkColors;
const globalTokenScale = 0.9;
const responsiveBaseScale = {
	spacing: {
		xs: 8,
		sm: 12,
		md: 16,
		lg: 24,
		xl: 32,
		xxl: 48,
	},
	typography: {
		xs: responsiveFontSize(12),
		sm: responsiveFontSize(14),
		base: responsiveFontSize(16),
		lg: responsiveFontSize(18),
		xl: responsiveFontSize(20),
		xxl: responsiveFontSize(24),
	},
	components: {
		cardRadius: 16,
		buttonHeight: 48,
		inputHeight: 44,
		iconSize: 24,
	},
};

const scaledSizeTokens = Object.fromEntries(
	Object.entries(defaultConfig.tokens.size).map(([token, value]) => {
		if (typeof value !== 'number') {
			return [token, value];
		}

		return [token, Math.max(1, Math.round(value * globalTokenScale))];
	})
);

// Extend default tokens with marketing website colors
const extendedTokens = {
	...defaultConfig.tokens,
	color: {
		...defaultConfig.tokens.color,
		// Marketing website color tokens
		background: lightColors.background,
		foreground: lightColors.foreground,
		card: lightColors.card,
		cardForeground: lightColors.cardForeground,
		popover: lightColors.popover,
		popoverForeground: lightColors.popoverForeground,
		primary: lightColors.primary,
		primaryForeground: lightColors.primaryForeground,
		primaryGlow: lightColors.primaryGlow,
		secondary: lightColors.secondary,
		secondaryForeground: lightColors.secondaryForeground,
		muted: lightColors.muted,
		mutedForeground: lightColors.mutedForeground,
		accent: lightColors.accent,
		accentForeground: lightColors.accentForeground,
		destructive: lightColors.destructive,
		destructiveForeground: lightColors.destructiveForeground,
		border: lightColors.border,
		input: lightColors.input,
		ring: lightColors.ring,
		// Keep existing color scales for compatibility
		grey0: '#f9f9f9',
		grey1: '#f0f0f0',
		grey2: '#d9d9d9',
		grey3: '#bfbfbf',
		grey4: '#8c8c8c',
		grey5: '#595959',
		grey6: '#262626',
		grey7: '#1f1f1f',
		grey8: '#141414',
		grey9: '#0f0f0f',
		grey10: '#0a0a0a',
		grey11: '#050505',
		grey12: '#000000',
		blue0: '#e6f7ff',
		blue1: '#bae7ff',
		blue2: '#91d5ff',
		blue3: '#69c0ff',
		blue4: '#40a9ff',
		blue5: '#1890ff',
		blue6: '#096dd9',
		blue7: '#0050b3',
		blue8: '#003a8c',
		blue9: '#002766',
		blue10: '#001d40',
		blue11: '#001319',
		blue12: '#000a0f',
		red0: '#fff1f0',
		red1: '#ffccc7',
		red2: '#ffa39e',
		red3: '#ff7875',
		red4: '#ff4d4f',
		red5: '#f5222d',
		red6: '#cf1322',
		red7: '#a8071a',
		red8: '#820014',
		red9: '#5c0011',
		red10: '#3f000c',
		red11: '#2a0008',
		red12: '#1a0005',
		yellow0: '#feffe6',
		yellow1: '#ffffb8',
		yellow2: '#fffb8f',
		yellow3: '#fff566',
		yellow4: '#ffec3d',
		yellow5: '#fadb14',
		yellow6: '#d4b106',
		yellow7: '#ad8b00',
		yellow8: '#876800',
		yellow9: '#614700',
		yellow10: '#3d2f00',
		yellow11: '#291f00',
		yellow12: '#1a1300',
		green0: '#f6ffed',
		green1: '#d9f7be',
		green2: '#b7eb8f',
		green3: '#95de64',
		green4: '#73d13d',
		green5: '#52c41a',
		green6: '#389e0d',
		green7: '#237804',
		green8: '#135200',
		green9: '#092b00',
		green10: '#061f00',
		green11: '#041500',
		green12: '#020d00',
	},
	radius: {
		...defaultConfig.tokens.radius,
		// Custom radius values matching marketing website (--radius: 0.75rem = 12px)
		'sm': 8, // calc(var(--radius) - 4px)
		'md': 10, // calc(var(--radius) - 2px)
		'lg': 12, // var(--radius)
		'xl': 16, // calc(var(--radius) + 4px)
		'2xl': 20, // calc(var(--radius) + 8px)
		'card': responsiveBaseScale.components.cardRadius,
	},
	space: {
		...defaultConfig.tokens.space,
		'xs': responsiveBaseScale.spacing.xs,
		'sm': responsiveBaseScale.spacing.sm,
		'md': responsiveBaseScale.spacing.md,
		'lg': responsiveBaseScale.spacing.lg,
		'xl': responsiveBaseScale.spacing.xl,
		'2xl': responsiveBaseScale.spacing.xxl,
	},
	size: {
		...scaledSizeTokens,
		'input': responsiveBaseScale.components.inputHeight,
		'button': responsiveBaseScale.components.buttonHeight,
		'icon': responsiveBaseScale.components.iconSize,
	},
	fontSize: {
		...(defaultConfig.tokens as any).fontSize ? Object.fromEntries(
			Object.entries((defaultConfig.tokens as any).fontSize).map(([k, v]) => [k, responsiveFontSize(Number(v))])
		) : {},
		// Typography scale aligned with useResponsiveWeb base scale
		'xs': responsiveBaseScale.typography.xs,
		'sm': responsiveBaseScale.typography.sm,
		'base': responsiveBaseScale.typography.base,
		'lg': responsiveBaseScale.typography.lg,
		'xl': responsiveBaseScale.typography.xl,
		'2xl': responsiveBaseScale.typography.xxl,
		'3xl': responsiveFontSize(28),
		'4xl': responsiveFontSize(32),
		'5xl': responsiveFontSize(43),
	},
	fontWeight: {
		...((defaultConfig.tokens as any).fontWeight || {}),
		// Font weights
		light: '300',
		normal: '400',
		medium: '500',
		semibold: '600',
		bold: '700',
		extrabold: '800',
	},
	lineHeight: {
		...((defaultConfig.tokens as any).lineHeight || {}),
		// Line heights
		tight: 1.25,
		normal: 1.5,
		relaxed: 1.75,
	},
};


const headingFont = createFont({
  family: defaultConfig.fonts.heading.family,
  size: {
    ...Object.fromEntries(
      Object.entries(defaultConfig.fonts.heading.size).map(([k, v]) => [k, responsiveFontSize(Number(v))])
    ),
    5: responsiveFontSize(11), // H6
    6: responsiveFontSize(12), // H5
    7: responsiveFontSize(13), // H4
    8: responsiveFontSize(14), // H3
    9: responsiveFontSize(20), // H2
    10: responsiveFontSize(24), // H1
    true: responsiveFontSize(14),
  },
  lineHeight: {
    ...defaultConfig.fonts.heading.lineHeight,
    5: 22,
    6: 24,
    7: 26,
    8: 28,
    9: 34,
    10: 38,
    true: 28,
  },
  weight: {
    ...defaultConfig.fonts.heading.weight,
    5: '600',
    6: '600',
    7: '600',
    8: '700',
    9: '700',
    10: '700',
    true: '700',
  },
  letterSpacing: defaultConfig.fonts.heading.letterSpacing,
});

const bodyFont = createFont({
  family: defaultConfig.fonts.body.family,
  size: Object.fromEntries(
    Object.entries(defaultConfig.fonts.body.size).map(([k, v]) => [k, responsiveFontSize(Number(v))])
  ),
  lineHeight: defaultConfig.fonts.body.lineHeight,
  weight: defaultConfig.fonts.body.weight,
  letterSpacing: defaultConfig.fonts.body.letterSpacing,
});

export const config = createTamagui({
	...defaultConfig,
	tokens: extendedTokens,
	// Disable fastSchemeChange to fix theme sync issues on native (iOS) - known to cause components
	// to show opposite/wrong theme when switching
	settings: {
		...defaultConfig.settings,
		fastSchemeChange: false,
	},
	// Override fonts with responsive sizes
	fonts: {
		...defaultConfig.fonts,
		heading: headingFont,
		body: bodyFont,
	},
	themes: {
		...themes,
		// Override theme colors with marketing website colors.
		// borderColor nudged toward SchemaComponent Field Type select tokens
		// (see tamagui-colors.ts input/border). Prefer AppSelect for new dropdowns.
		light: {
			...themes.light,
			...lightColors,
			backgroundHover: lightColors.secondary,
			backgroundPress: lightColors.muted,
			borderColor: lightColors.border,
		},
		dark: {
			...themes.dark,
			...darkColors,
			backgroundHover: darkColors.secondary,
			backgroundPress: darkColors.muted,
			borderColor: darkColors.border,
		},
	},
	// Skip optimization for heavy components
	exclude: ['SocialMediaScheduler'],
});
